import { useTranslation } from 'react-i18next';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

import { isMessageV2DTO } from 'support/helpers/transactions/transactions';
import { MessageV2DTO, SenderEnvelopeDTO, TransactionOriginApp } from 'support/types';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';

type UseDocumentSourceParams = {
  document: MessageV2DTO | SenderEnvelopeDTO | undefined;
  documentOriginApp: TransactionOriginApp;
};

export const useDocumentSource = ({ document, documentOriginApp }: UseDocumentSourceParams) => {
  const { t } = useTranslation();
  const isMessage = document && isMessageV2DTO(document);

  if (!isMessage) {
    return null;
  }

  return (
    <div className="flex gap-1">
      <span>{t(getDocumentOriginAppLabelKey(documentOriginApp))}</span>
      <Tooltip>
        <TooltipTrigger>
          <InformationCircleIcon className="ml-1 size-4 text-gray-400 hover:text-procuros-green-500" />
        </TooltipTrigger>
        <TooltipContent>{t(getDocumentOriginAppTooltipKey(documentOriginApp))}</TooltipContent>
      </Tooltip>
    </div>
  );
};

const getDocumentOriginAppLabelKey = (originApp: TransactionOriginApp): string => {
  /* i18n - For translation key extraction:
    t('webedi:sidebar.properties.documentOriginApp.PORTAL_OCR')
    t('webedi:sidebar.properties.documentOriginApp.UNATTENDED_OCR')
    t('webedi:sidebar.properties.documentOriginApp.UNATTENDED_STRUCTURED_FILE')
    t('webedi:sidebar.properties.documentOriginApp.ORDER_FORM')
    t('webedi:sidebar.properties.documentOriginApp.PORTAL_WEB_EDI')
    t('webedi:sidebar.properties.documentOriginApp.DOCUMENT_UNDERSTANDING_AGENT')
    t('webedi:sidebar.properties.documentOriginApp.default')
  */

  switch (originApp) {
    case 'PORTAL_OCR':
      return 'webedi:sidebar.properties.documentOriginApp.PORTAL_OCR';
    case 'UNATTENDED_OCR':
      return 'webedi:sidebar.properties.documentOriginApp.UNATTENDED_OCR';
    case 'UNATTENDED_STRUCTURED_FILE':
      return 'webedi:sidebar.properties.documentOriginApp.UNATTENDED_STRUCTURED_FILE';
    case 'ORDER_FORM':
      return 'webedi:sidebar.properties.documentOriginApp.ORDER_FORM';
    case 'PORTAL_WEB_EDI':
      return 'webedi:sidebar.properties.documentOriginApp.PORTAL_WEB_EDI';
    case 'DOCUMENT_UNDERSTANDING_AGENT':
      return 'webedi:sidebar.properties.documentOriginApp.DOCUMENT_UNDERSTANDING_AGENT';
    default:
      return 'webedi:sidebar.properties.documentOriginApp.default';
  }
};

const getDocumentOriginAppTooltipKey = (originApp: TransactionOriginApp): string => {
  /* i18n - For translation key extraction:
    t('webedi:sidebar.properties.documentOriginAppTooltip.PORTAL_OCR')
    t('webedi:sidebar.properties.documentOriginAppTooltip.UNATTENDED_OCR')
    t('webedi:sidebar.properties.documentOriginAppTooltip.UNATTENDED_STRUCTURED_FILE')
    t('webedi:sidebar.properties.documentOriginAppTooltip.ORDER_FORM')
    t('webedi:sidebar.properties.documentOriginAppTooltip.PORTAL_WEB_EDI')
    t('webedi:sidebar.properties.documentOriginAppTooltip.DOCUMENT_UNDERSTANDING_AGENT')
    t('webedi:sidebar.properties.documentOriginAppTooltip.default')
  */

  switch (originApp) {
    case 'PORTAL_OCR':
      return 'webedi:sidebar.properties.documentOriginAppTooltip.PORTAL_OCR';
    case 'UNATTENDED_OCR':
      return 'webedi:sidebar.properties.documentOriginAppTooltip.UNATTENDED_OCR';
    case 'UNATTENDED_STRUCTURED_FILE':
      return 'webedi:sidebar.properties.documentOriginAppTooltip.UNATTENDED_STRUCTURED_FILE';
    case 'ORDER_FORM':
      return 'webedi:sidebar.properties.documentOriginAppTooltip.ORDER_FORM';
    case 'PORTAL_WEB_EDI':
      return 'webedi:sidebar.properties.documentOriginAppTooltip.PORTAL_WEB_EDI';
    case 'DOCUMENT_UNDERSTANDING_AGENT':
      return 'webedi:sidebar.properties.documentOriginAppTooltip.DOCUMENT_UNDERSTANDING_AGENT';
    default:
      return 'webedi:sidebar.properties.documentOriginAppTooltip.default';
  }
};
